/* eslint-disable react-hooks/exhaustive-deps */
import {formatSecondsToMinSec, handleError} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel} from "hooks"
import {useCallback, useEffect} from "react"
import {RecordingPlaybackProvider} from "context/RecordingPlaybackContext"
import {studentCallLogService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseDepartmentId} from "types/departments"
import {FilterKey, FilterList} from "types/filter"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {RecordingPlayback} from "../RecordingPlayback"
import {CallLogStatusOptions, StudentCallLog} from "types/student-call-log"
import moment from "moment"

type Props = KlassappTableProps & {
    filterKey?: FilterKey
    filterParams?: {
        filters: FilterList
        search?: string
    }
    departmentId?: BaseDepartmentId
    studentProfileId?: number
}

function StudentCallLogList(props: Props) {
    const model = useModel()
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        orderField,
        isLoadedTableFuncs,
        dispatch,
        dispatchFunc
    } = props
    const {filterKey, filterParams, departmentId} = props

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order, filterParams])

    const getFilterKey = useCallback(() => {
        return filterKey
    }, [filterKey])

    const getData = useCallback(async () => {
        try {
            dispatch({isLoading: true})

            const storageData = model.getStorageFilter(filterKey)
            let orderFieldData = orderField
            if (storageData?.colOrd) {
                const columns: any[] = getColumns()
                const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
                orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
            }

            const payload = {
                range: {
                    pageSize: pageSize,
                    page: page
                },
                departmentId,
                ...filterParams,
                sort: orderFieldData?.field
                    ? {
                          orderBy: orderFieldData.field,
                          orderDir: orderFieldData.order
                      }
                    : undefined
            }
            const {data, total} = await studentCallLogService.list(payload)
            const formattedDocuments = data.map((item: StudentCallLog) => {
                return {
                    ...item,
                    date: moment.utc(item.createdAt).local().format(model.getUserDateFormat()),
                    time: moment.utc(item.createdAt).local().format(model.getUserTimeFormat()),
                    callLength: formatSecondsToMinSec(item.duration),
                    isInbound: item.isInbound ? "Inbound" : "Outbound"
                }
            })
            dispatch({data: formattedDocuments, total})
        } catch (err) {
            handleError(err)
        } finally {
            dispatch({isLoading: false})
        }
    }, [filterKey, filterParams, page, pageSize, orderField])

    const getFields = () => {
        const fields = [
            "Date",
            "Time",
            "Student ID",
            "Student Name",
            "Phone",
            "Staff Name",
            "Call Status",
            "Inbound/Outbound",
            "Call Length",
            "Recording"
        ]
        return fields
    }

    const getColumns = () => {
        const cols: any = [
            {
                title: "Date",
                field: "date",
                columnIndex: 1
            },
            {
                title: "Time",
                field: "time",
                columnIndex: 2
            },
            {
                title: "Student ID",
                field: "customProfileId",
                columnIndex: 3
            },
            {
                title: "Student Name",
                field: "studentFullName",
                columnIndex: 4
            },
            {
                title: "Phone",
                field: "phone",
                columnIndex: 5
            },
            {
                title: "Staff Name",
                field: "staffFullName",
                columnIndex: 6
            },
            {
                title: "Call Status",
                field: "status",
                columnIndex: 10,
                render: (status: string) => {
                    return CallLogStatusOptions.find((option) => option.id === status)?.name || status
                }
            },
            {
                title: "Inbound/Outbound",
                field: "isInbound",
                columnIndex: 7
            },
            {
                title: "Call Length",
                field: "callLength",
                columnIndex: 8
            },
            {
                title: "Recording",
                field: "recordingUrl",
                columnIndex: 9,
                render: (recordingUrl: string | undefined) =>
                    recordingUrl ? <RecordingPlayback url={recordingUrl} /> : null
            },
            {
                title: "Error Message",
                field: "errorMessage",
                columnIndex: 11
            }
        ]

        return cols
    }

    return (
        <RecordingPlaybackProvider>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
            />
        </RecordingPlaybackProvider>
    )
}

export const StudentCallLogListView = KlassappTableHOC(StudentCallLogList)
